/**
 * 自定义封装http库类
 * 支持接口自定义超时时间、loading字段自关闭、headers自定义等
 *
 * post、get、put、delete 方法参数详解
 * url: 接口地址
 * data: 数据
 * request: 配置集合，详细看下方 request 描述
 *
 * request 方法参数详解（request 方法 支持自定义设置 method，该方法较为自由，大多数支持自定义）
 * url: 接口地址
 * request: 数据、method、配置等集合
 *
 * downloadBlob 方法 —— 文件流下载（参数与 request 一致）
 * url: 接口地址
 * request: 数据、method、配置等集合
 *
 * request 参数部分字段描述
 *
 *  headers:   Object    header请求头
 *  method:    String    请求方式（post、get、put、delete 方法不用设置）
 *  extra:     Object    loading 参数，请求前后回自处理，extra: { loading: false }
 *  timeout:   Number    自定义接口超时时间，毫秒
 *  isToken:   Boolean   是否请求头带上token, 默认 true
 */

import Qs from 'qs';
import axios from 'axios';
import store from '@/store';
import router from '@/router';
import vueBus from '@/utils/bus';

import { notification, message } from 'ant-design-vue';

// 站点 code
// const WEB_SITE_CODE = process.env.VUE_APP_WEB_SITE_CODE;

// 商户id
// const TENANT_ID = process.env.VUE_APP_WEB_TENANT_ID;

// 超时时间
const DEFAULTS_TIMEOUT = 60000;

// 请求域名
const BASE_URL = process.env.VUE_APP_BASE_API;

// 开启loading
const startLoading = options => {

    // loading
    if (options.extra.loading !== undefined) {
        options.extra.loading = true;
    }
};

// 关闭loading
const stopLoading = options => {

    // loading
    if (options.extra.loading !== undefined) {

        // options.extend.loading = false
        setTimeout(() => {
            options.extra.loading = false;
        }, 500);
    }
};

// 下载流文件
const downloadFile = (res, fileName) => {
    console.log('文件流导出', res);
    return new Promise((resolve, reject) => {
        const tempBlob = new Blob([res.data], { type: 'application/json' });

        // 通过 FileReader 读取这个 blob
        const reader = new FileReader();

        // 此处对fileReader读出的结果进行JSON解析
        // 可能会出现错误，需要进行捕获
        reader.onload = e => {
            const response = e.target.result;
            try {
                const json = JSON.parse(response);
                console.error('导出错误，异常', json.success);
                if (typeof json.success === 'boolean' && !json.success) {
                    const messageStr = `异常状态码：${json.code}，异常信息：${json.message || '接口异常，请联系管理员解决！'
                        }`;
                    message.error(messageStr);
                    res.data = json;
                    reject(res);
                }
            }
            catch (err) {

                // 该异常为无法将字符串转为json
                // 说明返回的数据是一个流文件
                const content = res.data;
                const blob = new Blob([content]);

                // 对于<a>标签，只有 Firefox 和 Chrome（内核） 支持 download 属性
                // IE10以上支持blob但是依然不支持download
                if ('download' in document.createElement('a')) {

                    // 支持a标签download的浏览器
                    const link = document.createElement('a'); // 创建a标签
                    link.download = fileName; // a标签添加属性
                    link.style.display = 'none';
                    link.href = URL.createObjectURL(blob);
                    document.body.appendChild(link);
                    link.click(); // 执行下载
                    URL.revokeObjectURL(link.href); // 释放url
                    document.body.removeChild(link); // 释放标签
                }
                else {

                    // 其他浏览器
                    navigator.msSaveBlob(blob, fileName);
                }
                resolve({
                    code: 200,
                    success: true,
                });
            }
        };

        reader.readAsText(tempBlob);
    });
};

export class HttpRequest {

    /**
     * 初始化
     * @param baseUrl 访问基本路径
     */
    constructor(baseUrl = BASE_URL) {
        this._baseUrl = baseUrl;
    }

    // 获取默认配置
    _getInsideConfig() {
        return {
            baseURL: this._baseUrl,
            headers: {},
        };
    }

    /**
     * 设置拦截器
     * @param instance axios实例
     * @param options 请求参数
     * @private
     */
    _interceptors(instance, options) {

        // 请求拦截
        instance.interceptors.request.use(
            config => {
                // 设置token
                const token = store.getters.token;
                if (config.isToken && token) {
                    // 需要设置 token 并token 存在
                    config.headers['X-School-Token'] = token;
                }

                /** * 设置商户 id、站点 code ***/
                // config.headers['tenant-id'] = TENANT_ID;
                // config.headers['website-code'] = WEB_SITE_CODE;
                /** * 设置商户 id、站点 code ***/

                // 设置时间戳：get请求
                if (config.method === 'get') {
                    if (config.url.indexOf('sys/dict/getDictItems') < 0) {
                        config.params = {
                            _t: Date.parse(new Date()) / 1000,
                            ...config.params,
                        };
                    }
                }

                return config;
            },

            error => Promise.reject(error)
        );

        // 响应拦截
        instance.interceptors.response.use(
            response => {
                if (options.isDownloadBlob) {
                    return response;
                }
                return response.data;
            },
            error => {
                console.error('异常：：：', error);

                // 网络超时异常处理
                if (error.code === 'ECONNABORTED' || error.message === 'Network Error') {
                    message.error('网络超时');
                    Promise.reject(error);
                }

                // 异常提示处理
                switch (Number(error.response.status)) {
                    case 401:
                        // 未授权，重新登录
                        message.error(`错误码：${error.response.status}，${error.response.data.message}`);
                        break;
                    case 404:
                        // todo 无资源，跳转404页面
                        notification.error({ message: '系统提示', description: '很抱歉，资源未找到!', duration: 4 });
                        break;
                    case 500:
                        // 登录失效
                        message.error(`错误码：${error.response.status}，${error.response.data.message}`);
                        break;
                    case 502:
                        // todo 服务异常，跳转服务异常页
                        break;
                    case 504:
                        // 网络超时
                        notification.error({ message: '系统提示', description: '网络超时' });
                        break;
                    default:
                        // 未知异常
                        notification.error({
                            message: '系统提示',
                            description: error.response.data
                                ? error.response.data.message || '请求异常，请联系管理员'
                                : '请求异常，请联系管理员',
                            duration: 4,
                        });
                        break;
                }
                return Promise.reject(error.response.data);
            }
        );
    }

    /**
     * 设置axios请求
     * @param options
     * @private
     */
    _handle(options) {

        // loading设置
        options.extra = options.extra || {};

        // message显示设置
        options.isNoMessage = options.isNoMessage || false;

        // 是否文件流下载操作，默认 false
        options.isDownloadBlob = options.isDownloadBlob || false;

        // 文件流下载，文件名
        options.fileName = options.fileName || '';

        // 是否需要 token
        options.isToken = typeof options.isToken === 'boolean' ? options.isToken : true;

        startLoading(options);

        const instance = axios.create();

        // 超时时间
        instance.defaults.timeout = DEFAULTS_TIMEOUT;

        instance.defaults.paramsSerializer = params => Qs.stringify(params, { arrayFormat: 'brackets' });


        if (options.timeout) {
            instance.defaults.timeout = options.timeout;
        }

        // 合并默认配置
        options = Object.assign(this._getInsideConfig(), options);

        // 注入拦截器
        this._interceptors(instance, options);

        return instance(options)
            .then(res => {
                stopLoading(options);

                // 下载流文件
                if (options.isDownloadBlob) {
                    return downloadFile(res, options.fileName);
                }

                // 判断接口是否抛出业务异常
                if (!res.success) {
                    switch (Number(res.code)) {
                        case 505:
                            // 需登录，弹出登录弹窗
                            console.log('需登录，弹出登录弹窗')
                            break;
                        case 506:
                            // 异常，跳转进入404页面
                            router.push('/404');
                            break;
                        // case 508:
                        //     // 支付不能重复操作，跳到订单详情
                        //     router.replace({
                        //         name: 'OrderPayFinish',
                        //         params: router.app.$route.params,
                        //     });
                        //     break;
                        default:
                            // 判断是否不需要message提示
                            if (!options.isNoMessage) {
                                if (Number(res.code) === 511) {
                                    message.warning(res.message || '业务异常，请联系管理员解决异常！');
                                }
                                else {
                                    const messageStr = `异常状态码：${res.code}，异常信息：${res.message || '接口异常，请联系管理员解决！'
                                        }`;
                                    message.error(messageStr);
                                }
                            }
                            break;
                    }

                    return Promise.reject(res);
                }
                return Promise.resolve(res);

            })
            .catch(error => {
                stopLoading(options);
                return Promise.reject(error);
            });
    }

    // 自动处理loading
    request(url, request = {}) {
        return this._handle({
            url,
            ...request,
        });
    }

    post(url, data, request = {}) {
        return this._handle({
            url,
            method: 'post',
            data: data,
            ...request,
        });
    }

    get(url, data, request = {}) {
        return this._handle({
            url,
            method: 'get',
            params: data,
            ...request,
        });
    }

    delete(url, data, request = {}) {
        return this._handle({
            url,
            method: 'delete',
            params: data,
            ...request,
        });
    }

    put(url, data, request = {}) {
        return this._handle({
            url,
            method: 'put',
            data,
            ...request,
        });
    }

    // 下载流文件
    downloadBlob(url, request) {
        return this._handle({
            url,
            ...request,
            responseType: 'blob',
            isDownloadBlob: true,
        });
    }
}

export default {
    HttpRequest,
};
